import type { Dispatch, SetStateAction } from 'react';

import { useCallback, useEffect, useState } from 'react';

export type UseLocalStorageOptions<TValue> = {
    key: string;
    initialValue: TValue;
};

export type UseLocalStorageReturn<TValue> = [
    TValue,
    Dispatch<SetStateAction<TValue>>,
    () => void,
];

export function useLocalStorage<TValue>({
    key,
    initialValue,
}: UseLocalStorageOptions<TValue>): UseLocalStorageReturn<TValue> {
    const [storedValue, setStoredValue] = useState<TValue>(() => {
        try {
            const item
                = typeof window !== 'undefined'
                    ? window.localStorage.getItem(key)
                    : null;
            return item ? (JSON.parse(item) as TValue) : initialValue;
        }
        catch (error) {
            console.error('Failed to read from localStorage:', error);
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(storedValue));
        }
        catch (error) {
            console.error('Failed to write to localStorage:', error);
        }
    }, [key, storedValue]);

    const removeStoredValue = useCallback(() => {
        try {
            window.localStorage.removeItem(key);
        }
        catch (error) {
            console.error('Failed to remove from localStorage:', error);
        }
    }, [key]);

    return [storedValue, setStoredValue, removeStoredValue];
}
